// pages/dashboards/DashboardsPage.jsx
//
// Lists all dashboards for the company. Create, delete, and navigate to builder.
// Matches existing app patterns: useTheme, useNavigate, apiService, Tailwind.

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Plus,
  Tv,
  ExternalLink,
  Trash2,
  Edit3,
  Globe,
  Lock,
  RefreshCw,
  BarChart2,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

export default function DashboardsPage() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchDashboards();
  }, []);

  const fetchDashboards = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getDashboards();
      setDashboards(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await apiService.deleteDashboard(id);
      setDashboards((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreated = (dashboard) => {
    setShowCreateModal(false);
    navigate(`/dashboards/${dashboard.id}/builder`);
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"
      }`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1
              className={`text-2xl sm:text-3xl font-bold mb-1 ${
                isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"
              }`}
            >
              Dashboards
            </h1>
            <p
              className={`text-sm ${
                isDarkMode ? "text-[#94979C]" : "text-gray-500"
              }`}
            >
              Build and manage live dashboards for your TV displays
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchDashboards}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "hover:bg-gray-800 text-[#94979C] hover:text-gray-300"
                  : "hover:bg-gray-200 text-gray-500"
              }`}
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm"
            >
              <Plus size={16} />
              New Dashboard
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Loading skeletons */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className={`rounded-xl border p-6 animate-pulse ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700/50"
                    : "bg-white border-gray-200"
                }`}
              >
                <div
                  className={`h-5 w-2/3 rounded mb-3 ${
                    isDarkMode ? "bg-[#13161B]" : "bg-gray-200"
                  }`}
                />
                <div
                  className={`h-4 w-1/2 rounded mb-6 ${
                    isDarkMode ? "bg-[#13161B]" : "bg-gray-200"
                  }`}
                />
                <div
                  className={`h-8 w-full rounded ${
                    isDarkMode ? "bg-[#13161B]" : "bg-gray-200"
                  }`}
                />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && dashboards.length === 0 && (
          <div
            className={`rounded-xl border-2 border-dashed p-16 flex flex-col items-center justify-center text-center ${
              isDarkMode ? "border-gray-700/50" : "border-gray-300"
            }`}
          >
            <div
              className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
                isDarkMode ? "bg-[#13161B]" : "bg-gray-100"
              }`}
            >
              <LayoutDashboard
                size={32}
                className={isDarkMode ? "text-gray-600" : "text-gray-400"}
              />
            </div>
            <h3
              className={`text-lg font-semibold mb-2 ${
                isDarkMode ? "text-[#F7F7F7]" : "text-gray-700"
              }`}
            >
              No dashboards yet
            </h3>
            <p
              className={`text-sm mb-6 max-w-sm ${
                isDarkMode ? "text-[#94979C]" : "text-gray-400"
              }`}
            >
              Create your first dashboard and start sending live data to your TV
              displays.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm"
            >
              <Plus size={16} />
              Create Dashboard
            </button>
          </div>
        )}

        {/* Dashboard grid */}
        {!loading && dashboards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashboards.map((dashboard) => (
              <DashboardCard
                key={dashboard.id}
                dashboard={dashboard}
                isDarkMode={isDarkMode}
                deleting={deletingId === dashboard.id}
                onEdit={() => navigate(`/dashboards/${dashboard.id}/builder`)}
                onPreview={() =>
                  window.open(`/tv/${dashboard.shareToken}`, "_blank")
                }
                onDelete={() => handleDelete(dashboard.id, dashboard.name)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <CreateDashboardModal
          isDarkMode={isDarkMode}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}

// ─── Dashboard card ────────────────────────────────────────────────────────

function DashboardCard({
  dashboard,
  isDarkMode,
  deleting,
  onEdit,
  onPreview,
  onDelete,
}) {
  const widgetCount = dashboard.widgets?.length ?? 0;
  const tvCount = dashboard.tvDisplays?.length ?? 0;

  return (
    <div
      className={`rounded-xl border transition-all group ${
        isDarkMode
          ? "bg-[#0C0E12] border-gray-700/50 hover:border-gray-600/50"
          : "bg-white border-gray-200 hover:border-gray-300"
      }`}
    >
      {/* Card top — clickable to edit */}
      <div className="p-5 cursor-pointer" onClick={onEdit}>
        <div className="flex items-start justify-between mb-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isDarkMode ? "bg-blue-500/10" : "bg-blue-50"
            }`}
          >
            <BarChart2 size={20} className="text-blue-500" />
          </div>
          <div className="flex items-center gap-1.5">
            {dashboard.isPublic ? (
              <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500 border border-green-500/20">
                <Globe size={11} />
                Public
              </span>
            ) : (
              <span
                className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
                  isDarkMode
                    ? "bg-[#13161B] text-[#94979C] border-gray-700/50"
                    : "bg-gray-100 text-gray-500 border-gray-200"
                }`}
              >
                <Lock size={11} />
                Private
              </span>
            )}
          </div>
        </div>

        <h3
          className={`font-semibold text-base mb-1 group-hover:text-blue-500 transition-colors ${
            isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"
          }`}
        >
          {dashboard.name}
        </h3>

        {dashboard.description && (
          <p
            className={`text-sm mb-3 line-clamp-2 ${
              isDarkMode ? "text-[#94979C]" : "text-gray-500"
            }`}
          >
            {dashboard.description}
          </p>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-4 mt-3">
          <span
            className={`flex items-center gap-1.5 text-xs ${
              isDarkMode ? "text-[#94979C]" : "text-gray-500"
            }`}
          >
            <LayoutDashboard size={13} />
            {widgetCount} widget{widgetCount !== 1 ? "s" : ""}
          </span>
          {tvCount > 0 && (
            <span className="flex items-center gap-1.5 text-xs text-blue-500">
              <Tv size={13} />
              {tvCount} TV{tvCount !== 1 ? "s" : ""}
            </span>
          )}
          {dashboard.refreshInterval && (
            <span
              className={`flex items-center gap-1.5 text-xs ${
                isDarkMode ? "text-[#94979C]" : "text-gray-400"
              }`}
            >
              <RefreshCw size={11} />
              {dashboard.refreshInterval}s
            </span>
          )}
        </div>
      </div>

      {/* Card actions */}
      <div
        className={`flex items-center gap-1 px-4 py-3 border-t ${
          isDarkMode ? "border-[#22262F]" : "border-gray-100"
        }`}
      >
        <button
          onClick={onEdit}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            isDarkMode
              ? "hover:bg-gray-800 text-gray-300"
              : "hover:bg-gray-100 text-gray-600"
          }`}
        >
          <Edit3 size={14} />
          Edit
        </button>
        <button
          onClick={onPreview}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            isDarkMode
              ? "hover:bg-gray-800 text-gray-300"
              : "hover:bg-gray-100 text-gray-600"
          }`}
        >
          <ExternalLink size={14} />
          Preview
        </button>
        <button
          onClick={onDelete}
          disabled={deleting}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ml-auto ${
            deleting
              ? "opacity-40 cursor-not-allowed"
              : "hover:bg-red-500/10 text-red-400"
          }`}
        >
          <Trash2 size={14} />
          {deleting ? "Deleting…" : "Delete"}
        </button>
      </div>
    </div>
  );
}

// ─── Create dashboard modal ────────────────────────────────────────────────

function CreateDashboardModal({ isDarkMode, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [refreshInterval, setRefreshInterval] = useState("60");
  const [isPublic, setIsPublic] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const res = await apiService.createDashboard({
        name: name.trim(),
        description: description.trim() || undefined,
        refreshInterval: parseInt(refreshInterval) || 60,
        isPublic,
      });
      if (res.success) onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && name.trim()) handleCreate();
    if (e.key === "Escape") onClose();
  };

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/40 ${
    isDarkMode
      ? "bg-[#13161B] border-gray-700/50 text-[#F7F7F7] placeholder-[#94979C]"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  const labelClass = `block text-sm font-medium mb-1.5 ${
    isDarkMode ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-md rounded-2xl shadow-2xl ${
          isDarkMode
            ? "bg-[#0C0E12] border border-gray-700/50"
            : "bg-white border border-gray-200"
        }`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="p-6">
          <h2
            className={`text-xl font-bold mb-1 ${
              isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"
            }`}
          >
            New Dashboard
          </h2>
          <p
            className={`text-sm mb-6 ${
              isDarkMode ? "text-[#94979C]" : "text-gray-500"
            }`}
          >
            You'll be taken straight to the builder after creating.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className={labelClass}>
                Name <span className="text-red-400">*</span>
              </label>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Sales Team KPIs"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Description</label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Refresh Interval (seconds)</label>
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(e.target.value)}
                className={inputClass}
              >
                <option value="30">30 seconds</option>
                <option value="60">1 minute</option>
                <option value="300">5 minutes</option>
                <option value="900">15 minutes</option>
              </select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Public (TV accessible)
                </p>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-[#94979C]" : "text-gray-400"
                  }`}
                >
                  Required for TV displays — no login needed
                </p>
              </div>
              <button
                onClick={() => setIsPublic((v) => !v)}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  isPublic
                    ? "bg-blue-600"
                    : isDarkMode
                      ? "bg-gray-700"
                      : "bg-gray-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    isPublic ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        <div
          className={`flex items-center justify-end gap-3 px-6 py-4 border-t ${
            isDarkMode ? "border-[#22262F]" : "border-gray-100"
          }`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isDarkMode
                ? "hover:bg-gray-800 text-gray-300"
                : "hover:bg-gray-100 text-gray-600"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim() || creating}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            {creating ? "Creating…" : "Create & Open Builder"}
          </button>
        </div>
      </div>
    </div>
  );
}
